import { Injectable, Signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AddressByGoogle, DType, NextLocator } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { merge } from 'lodash-es';

import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { LatLng } from 'leaflet';
import { AbsenceCalendarDialogComponent } from './absence-calendar-dialog/absence-calendar-dialog.component';
import { AddUserEventDialogComponent } from './add-user-event-dialog/add-user-event-dialog.component';
import { AddressListDialogComponent } from './address-list-dialog/address-list-dialog.component';
import { AliasDialogComponent } from './alias-dialog/alias-dialog.component';
import { CancelTaskDialogComponent } from './cancel-task-dialog/cancel-task-dialog.component';
import { CancelWorkshiftDialogComponent } from './cancel-workshift-dialog/cancel-workshift-dialog.component';
import { CategoryNode, CategoryTreeDialogComponent } from './category-tree-dialog/category-tree-dialog.component';
import { ChangeRepoNameComponent } from './change-repo-name-dialog/change-repo-name.component';
import { ClipboardDataDialogComponent } from './clipboard-data-dialog/clipboard-data-dialog.component';
import { CloneServiceDataDialogComponent } from './clone-service-data-dialog/clone-service-data-dialog.component';
import { ConfirmationDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { ConfirmationConfig } from './confirm-dialog/confirm-dialog.types';
import { ConfirmationWithCheckDialogComponent } from './confirm-with-check-dialog/confirm-with-check-dialog.component';
import { ConfirmationWithCheckConfig } from './confirm-with-check-dialog/confirm-with-check-dialog.types';
import { ConfirmationWithCountdownDialogComponent } from './confirm-with-countdown-dialog/confirm-with-countdown-dialog.component';
import { ConfirmationWithCountdownConfig } from './confirm-with-countdown-dialog/confirm-with-countdown-dialog.types';
import { CreateProfileDialogComponent } from './create-profile-dialog/create-profile-dialog.component';
import { DTypeMasterDataDialogComponent } from './dtype-masterdata-dialog/dtype-masterdata-dialog.component';
import { DTypeMasterdataDialogConfig } from './dtype-masterdata-dialog/dtype-masterdata-dialog.types';
import { EditCalendarDialogComponent } from './edit-calendar-dialog/edit-calendar-dialog.component';
import { EditPlanningAreaComponent } from './edit-planning-area-dialog/edit-planning-area-dialog.component';
import { EditServiceJobDialogComponent } from './edit-service-job-dialog/edit-service-job-dialog.component';
import { EditUserEventsDialogComponent } from './edit-user-events-dialog/edit-user-events-dialog.component';
import { ErrorDialogComponent } from './error-dialog/error-dialog.component';
import { ErrorDialogConfig } from './error-dialog/error-dialog.types';
import { GeopositionDialogComponent } from './geoposition-dialog/geoposition-dialog.component';
import { IconPickerDialogComponent } from './iconpicker-dialog/iconpicker-dialog.component';
import { InfoDialogComponent } from './info-dialog/info-dialog.component';
import { InfoDialogConfig } from './info-dialog/info-dialog.types';
import { LocationChangeDialogComponent } from './location-change-dialog/location-change-dialog.component';
import { ModifyTaskTypeDialogComponent } from './modify-task-type-dialog/modify-task-type-dialog.component';
import { MonthConceptsDialogComponent } from './month-concepts-dialog/generic/month-concepts-dialog.component';
import { HotelMonthConceptsDialogComponent } from './month-concepts-dialog/hotel/hotel-month-concepts-dialog.component';
import { MoreInfoEventsDialogComponent } from './more-info-events-dialog/more-info-events-dialog.component';
import { NewFormDialog } from './new-form-dialog/new-form-dialog.component';
import { NewFormReplyDialog } from './new-form-reply-dialog/new-form-reply-dialog.component';
import { NewMasterDataDialog } from './new-masterdata-dialog/new-masterdata-dialog.component';
import { NewReviewAbsenceDialogComponent } from './new-review-absence-dialog/new-review-absence-dialog.component';
import { NewTaskTypeDataDialogComponent } from './new-tasktype-dialog/new-tasktype-dialog.component';
import { PresenceConceptDialogComponent } from './presence-concept-dialog/presence-concept-dialog.component';
import { PreviewFileDialogComponent } from './preview-file-dialog/preview-file-dialog.component';
import { PwdChangeAdminDialog } from './pwd-change-admin-dialog/pwd-change-admin-dialog.component';
import { RequestGoLiveDialogComponent } from './request-go-live-dialog/request-go-live-dialog.component';
import { RequestGoLiveBody, RequestGoLiveDialogData } from './request-go-live-dialog/request-go-live-dialog.types';
import { ResourcesSelectAreaDialog } from './resources-select-area-dialog/resources-select-area-dialog.component';
import { ReviewAbsenceDialogComponent } from './review-absence-dialog/review-absence-dialog.component';
import { ScheduleGoLiveDialogComponent } from './schedule-go-live-dialog/schedule-go-live-dialog.component';
import { SearchResourcesDialogComponent } from './search-resources-dialog/search-resources-dialog.component';
import { SearchUserEventDialogComponent } from './search-user-event-dialog/search-user-event-dialog.component';
import { SelectGoalDateComponent } from './select-goal-date/select-goal-date-dialog.component';
import { SelectLocatorDialogComponent } from './select-locator-dialog/select-locator-dialog.component';
import { SyncWorkplaceUsersDialog } from './sync-workplace-users-dialog/sync-workplace-users-dialog.component';
import { TaskDetailDialogComponent } from './task-detail-dialog/task-detail-dialog.component';
import { TaskFormDialogComponent } from './task-form-dialog/task-form-dialog.component';
import { TaskGoalDialogComponent } from './task-goal-dialog/task-goal-dialog.component';
import { TaskTypeFormDialogComponent } from './task-type-form-dialog/task-type-form-dialog.component';
import { TaskTypeFormDialogConfig } from './task-type-form-dialog/task-type-form-dialog.types';
import { TaskTypeGoalDialogComponent } from './task-type-goal-dialog/task-type-goal-dialog.component';
import { UploadExcelDialogComponent } from './upload-excel-dialog/upload-excel-dialog.component';
import { UserPrivilegeDialogComponent } from './user-privilege-dialog/user-privilege-dialog.component';
import { UserQrDialogComponent } from './user-qr-dialog/user-qr-dialog.component';
import { WorkdayEventListDialogComponent } from './workday-event-list-dialog/workday-event-list-dialog.component';
import { WorkplaceUserAssignmentDialogComponent } from './workplace-user-assignment-dialog/workplace-user-assignment-dialog.component';
import { WorkshiftUserEventsDialogComponent } from './workshift-user-events-dialog/workshift-user-events-dialog.component';
import { FuseConfirmationService } from '../../@fuse/services/confirmation/confirmation.service';
import { MasterData } from '../core/master-data/master-data.types';
import { NextTaskType, NextTaskTypeForm, NextTaskTypeGoal, NextTaskTypeGoalResume } from '../core/task-type/task-type.types';
import { CompanyUser } from '../core/user/companyUser.model';
import { NextCalendar, UserProfile } from '../modules/admin/projects/projects.types';
import { FormSummary, RetrieveMultiTaskEditReply, RetrieveTaskByIdReply, TaskGoal, TaskSearch, WorkdayGeopositionMarkerData, WorkdayReferenceGeopositionMarkerData, Task } from '../modules/admin/tasks/tasks.types';
import { EditMultiTaskDialogComponent } from '../modules/admin/tasks/edit-multitask-dialog/edit-multitask-dialog.component';
import { MissionUsersEventsDialogComponent } from './mission-users-events-dialog/mission-users-events-dialog.component';
import { UserNodePrivilegesForDisplay } from '../modules/admin/ou/ou.types';
import { Workshift, WorkshiftResource, WorkshiftUserEventSummary } from '../modules/admin/workshifts/workshifts.types';
import { PremisesLink } from '../modules/admin/projects/premises-management/premises.types';
import { AvailabilityControl, NextServicio, PlanningArea, ServiceJob, ServiceUserAssignment, ServiceWorkplaceUserAssignment, WorkDayEvent } from '../modules/admin/services/services.types';
import { Clipboard } from '../core/clipboard/clipboard.model';

@Injectable()
export class DialogsService {

    constructor(
        private _fuseConfirmationService: FuseConfirmationService,
        private _matDialog: MatDialog,
        private translocoService: TranslocoService,
    ) { }

    openErrorDialog(config: ErrorDialogConfig = {}): MatDialogRef<ErrorDialogComponent> {
        const _defaultConfig: ErrorDialogConfig = {
            title: this.translocoService.translate('error'),
            message: this.translocoService.translate('errorDialogMsg'),
            icon: {
                show: true,
                name: 'mat_outline:error',
                color: 'error'
            },
            actions: {
                accept: {
                    show: true,
                    label: this.translocoService.translate('accept'),
                    color: 'warn'
                },
            },
        };
        // Merge the user config with the default config
        const userConfig = merge({}, _defaultConfig, config);

        // Open the dialog
        return this._matDialog.open(ErrorDialogComponent, {
            autoFocus: true,
            disableClose: true,
            data: userConfig,
            panelClass: 'error-dialog-panel'
        });
    }

    openConfirmDialog(title: string, message: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title,
            message,
            actions: {
                confirm: {
                    label: this.translocoService.translate('accept')
                },
                cancel: {
                    label: this.translocoService.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

    openCustomConfirmDialog(config: ConfirmationConfig = {}) {
        const _defaultConfig: ConfirmationConfig = {
            title: this.translocoService.translate('warning'),
            message: this.translocoService.translate('warning'),
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation',
                color: 'warn'
            },
            actions: [
                {
                    label: this.translocoService.translate('cancel'),
                    value: 'cancel',
                    color: 'warn'
                },
                {
                    label: this.translocoService.translate('accept'),
                    value: 'accept',
                    color: 'primary'
                }
            ]
        };
        // Merge the user config with the default config
        const userConfig = merge({}, _defaultConfig, config);
        const dialog = this._matDialog.open(ConfirmationDialogComponent, {
            autoFocus: false,
            disableClose: true,
            data: userConfig,
            panelClass: 'confirmation-dialog-panel'
        });
        return new Promise<any>((resolve) => {
            dialog.afterClosed().subscribe((result) => {
                resolve(result);
            });
        });
    }

    openInfoDialog(config: InfoDialogConfig = {}): MatDialogRef<InfoDialogComponent> {
        const _defaultConfig: InfoDialogConfig = {
            title: this.translocoService.translate('warning'),
            message: this.translocoService.translate('warning'),
            icon: {
                show: true,
                name: 'mat_outline:info',
                color: 'primary'
            },
            actions: {
                accept: {
                    show: true,
                    label: this.translocoService.translate('accept'),
                    color: 'primary'
                },
            },
        };
        // Merge the user config with the default config
        const userConfig = merge({}, _defaultConfig, config);

        // Open the dialog
        return this._matDialog.open(InfoDialogComponent, {
            autoFocus: false,
            disableClose: true,
            data: userConfig,
            panelClass: 'info-dialog-panel'
        });
    }

    openDtypeMasterDataDialog(dType: MasterData, item?: any, readonly = false, config?: DTypeMasterdataDialogConfig): MatDialogRef<DTypeMasterDataDialogComponent> {
        const defaultConfig: DTypeMasterdataDialogConfig = {
            actions: {
                cancel: {
                    show: true,
                    type: 'stroked',
                    color: 'basic',
                    label: this.translocoService.translate('cancel'),
                },
                save: {
                    show: Boolean(item) ? dType.noUpdate ? false : true : dType.noInsert ? false : true,
                    type: 'flat',
                    color: 'primary',
                    label: this.translocoService.translate('save'),
                },
            }
        };
        const finalConfig: DTypeMasterdataDialogConfig = merge({}, defaultConfig, config);
        const data = { item, dType, config: finalConfig, update: !!item, readonly };
        return this._matDialog.open(DTypeMasterDataDialogComponent, {
            data,
            panelClass: 'dtype-masterdata-dialog-panel',
            width: '90vw',
            height: '80vh',
            maxWidth: '1536px',
        });
    }

    openTaskTypeFormDialog(taskType: NextTaskType, dType: DType, formsUris?: string[], item?: any, config?: TaskTypeFormDialogConfig): MatDialogRef<TaskTypeFormDialogComponent> {
        const data = { taskType, item, dType, config, update: !!item, formsUris};
        return this._matDialog.open(TaskTypeFormDialogComponent, {
            data,
            width: '90vw',
            height: '70vh'
        });
    }

    openConfirmWithcheckDialog(config?: ConfirmationWithCheckConfig): MatDialogRef<ConfirmationWithCheckDialogComponent, any> {
        const defaultConfig: ConfirmationWithCheckConfig = {
            title: 'Confirm action',
            message: 'Are you sure you want to confirm this action?',
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation',
                color: 'warn'
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Confirm',
                    color: 'warn'
                },
                cancel: {
                    show: true,
                    label: 'Cancel'
                }
            },
            dismissible: false
        };
        const finalConfig: ConfirmationWithCheckConfig = merge({}, defaultConfig, config);
        // Open the dialog
        return this._matDialog.open(ConfirmationWithCheckDialogComponent, {
            autoFocus: false,
            disableClose: !finalConfig.dismissible,
            data: finalConfig,
            panelClass: 'confirmation-with-check-dialog-panel'
        });
    }

    openConfirmWithCountdownDialog(config?: ConfirmationWithCountdownConfig): MatDialogRef<ConfirmationWithCountdownDialogComponent, any> {
        const defaultConfig: ConfirmationWithCountdownConfig = {
            title: 'Confirm action',
            message: 'Are you sure you want to confirm this action?',
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation',
                color: 'warn'
            },
            countdownMessage: 'The action will be effective in',
            seconds: 10,
            actions: {
                confirm: {
                    show: true,
                    label: 'Confirm',
                    color: 'warn'
                },
                cancel: {
                    show: true,
                    label: 'Cancel'
                }
            },
            dismissible: false
        };
        const finalConfig: ConfirmationWithCountdownConfig = merge({}, defaultConfig, config);
        // Open the dialog
        return this._matDialog.open(ConfirmationWithCountdownDialogComponent, {
            autoFocus: false,
            disableClose: !finalConfig.dismissible,
            data: finalConfig,
            panelClass: 'confirmation-with-countdown-dialog-panel'
        });
    }

    openAddressListDialog(addressList: AddressByGoogle[]): MatDialogRef<AddressListDialogComponent, any> {
        return this._matDialog.open(AddressListDialogComponent, {
            data: {
                addressList
            },
            width: '75vw',
            height: '60vh'
        });
    }

    openSearchResourcesDialog(service: NextServicio, planningArea: PlanningArea, usersAssigned: ServiceUserAssignment[], multiple: boolean = true):
        MatDialogRef<SearchResourcesDialogComponent, any> {
        return this._matDialog.open(SearchResourcesDialogComponent, {
            data: {
                planningArea,
                service,
                usersAssigned,
                multiple
            },
            width: '75vw',
            height: '70vh'
        });
    }

    openIconPickerDialog(svgIcon: string, optionNameSpaces: string[]): MatDialogRef<IconPickerDialogComponent, any> {
        return this._matDialog.open(IconPickerDialogComponent, {
            width: '70vw',
            height: '80vh',
            maxWidth: '1536px',
            panelClass: 'iconpicker-dialog-panel',
            data: {
                svgIcon,
                optionNameSpaces
            }
        });
    }

    openPwdChangeAdminDialog(user: CompanyUser): MatDialogRef<PwdChangeAdminDialog, any> {
        return this._matDialog.open(PwdChangeAdminDialog, {
            width: '50vw',
            height: '75vh',
            data: {
                user
            }
        });
    }

    openNewMasterDataDialog(): MatDialogRef<NewMasterDataDialog, any> {
        return this._matDialog.open(NewMasterDataDialog, {
            width: '45vw',
            height: '40vh',
        });
    }

    openNewFormDialog(): MatDialogRef<NewFormDialog, any> {
        return this._matDialog.open(NewFormDialog, {
            width: '45vw',
            height: '40vh',
        });
    }

    openNewTasktypeDataDialog(): MatDialogRef<NewTaskTypeDataDialogComponent, any> {
        return this._matDialog.open(NewTaskTypeDataDialogComponent, {
            width: '45vw',
            height: '40vh',
        });
    }

    openTaskTypeModifyDialog(taskType: NextTaskType, locator: NextLocator): MatDialogRef<ModifyTaskTypeDialogComponent, any> {
        return this._matDialog.open(ModifyTaskTypeDialogComponent, {
            data: {
                taskType,
                locator
            },
            panelClass: 'modify-task-type-dialog-panel',
            width: '75vw',
            height: '80vh',
            maxWidth: '1536px',
        });
    }

    openEditServiceJobDialog(job: ServiceJob, dtype: DType): MatDialogRef<EditServiceJobDialogComponent, any> {
        return this._matDialog.open(EditServiceJobDialogComponent, {
            data: {
                job,
                dtype,
                update: job ? true : false
            },
            panelClass: 'edit-service-job-dialog-panel',
            width: '75vw',
            height: '80vh',
        });
    }

    openEditPlanningAreaDialog(area: PlanningArea, resources: boolean, calendarList: NextCalendar[]) {
        return this._matDialog.open(EditPlanningAreaComponent, {
            data: {
                area,
                resources,
                calendarList
            },
            panelClass: 'edit-planning-area-dialog-panel',
            width: '70vw',
            height: '60vh'
        });
    }

    openCancelTaskDialog(tasksToCancel: string[], tasksCantCancel: string[]): MatDialogRef<CancelTaskDialogComponent, any> {
        return this._matDialog.open(CancelTaskDialogComponent, {
            data: {
                tasksToCancel,
                tasksCantCancel
            },
            width: '60vw',
            height: '45vh'
        });
    }

    openCancelWorkshiftDialog(workshiftsToCancel: string[], workshiftsCantCancel: string[], marca_code: string): MatDialogRef<CancelWorkshiftDialogComponent, any> {
        return this._matDialog.open(CancelWorkshiftDialogComponent, {
            data: {
                workshiftsToCancel,
                workshiftsCantCancel,
                marca_code
            },
            width: '60vw',
            height: '45vh'
        });
    }

    openMoreInfoEventsDialog(service: NextServicio, task: TaskSearch): MatDialogRef<MoreInfoEventsDialogComponent, any> {
        return this._matDialog.open(MoreInfoEventsDialogComponent, {
            data: {
                service,
                task
            },
            width: '75vw',
            height: '70vh'
        });
    }

    openEditMultiTask(service: NextServicio, multiTaskEditData: RetrieveMultiTaskEditReply, planningAreaId: string) {
        return this._matDialog.open(EditMultiTaskDialogComponent, {
            width: '50vw',
            height: '70vh',
            minWidth: '410px',
            minHeight: '400px',
            maxWidth: '780px',
            maxHeight: '800px',
            data: {
                service,
                multiTaskEditData,
                planningAreaId
            }
        });
    }

    openTaskFormDialog(form: Partial<FormSummary>, taskTypeForm: NextTaskTypeForm, task: Task, locator: NextLocator,
        readonly: boolean): MatDialogRef<TaskFormDialogComponent, any> {
        return this._matDialog.open(TaskFormDialogComponent, {
            data: {
                form,
                taskTypeForm,
                task,
                locator,
                readonly
            },
            panelClass: 'task-form-dialog-panel',
            width: '70vw',
            height: '70vh',
            maxWidth: '1536px',
        });
    }

    openCategoryTreeDialog(tree: CategoryNode[], uri: string, masterName: string, listOids: string[]): MatDialogRef<CategoryTreeDialogComponent, any> {
        return this._matDialog.open(CategoryTreeDialogComponent, {
            data: {tree, uri, masterName, listOids},
            width: '60vw',
            height: '70vh'
        });
    }

    openUserEventsDialog(user: CompanyUser): MatDialogRef<MissionUsersEventsDialogComponent, any> {
        return this._matDialog.open(MissionUsersEventsDialogComponent, {
            data: {user},
            width: '90vw',
            height: '90vh'
        });
    }

    openPreviewFileDialog(file: any, width: string, height: string): MatDialogRef<PreviewFileDialogComponent, any> {
        return this._matDialog.open(PreviewFileDialogComponent, {
            data: {file},
            width: width,
            height: height
        });
    }

    openCloneServiceDialog(): MatDialogRef<CloneServiceDataDialogComponent, any> {
        return this._matDialog.open(CloneServiceDataDialogComponent, {
            data: {},
            width: '60vw',
            height: '45vh'
        });
    }

    openNewFormReplyDialog(task: Task, forms: NextTaskTypeForm[]): MatDialogRef<NewFormReplyDialog, any> {
        return this._matDialog.open(NewFormReplyDialog, {
            data: { task, forms },
            width: '40vw',
            height: '30vh',
        });
    }

    openTaskTypeGoalDialog(dType: DType, existingGoals?: NextTaskTypeGoalResume[], item?: NextTaskTypeGoal): MatDialogRef<TaskTypeGoalDialogComponent> {
        const data = { dType, existingGoals, item, update: Boolean(item) };
        return this._matDialog.open(TaskTypeGoalDialogComponent, {
            data,
            width: '90vw',
            height: '80vh'
        });
    }

    openTaskGoalDialog(taskType: NextTaskType, taskDate: string, goals: TaskGoal[] = [], fromEditTask = false): MatDialogRef<TaskGoalDialogComponent> {
        const data = { taskTypeGoals: taskType.goals.filter(item => item.from <= taskDate && (!item.to || item.to >= taskDate)), goals, fromEditTask };
        return this._matDialog.open(TaskGoalDialogComponent, {
            data,
            width: '70vw',
            height: '60vh'
        });
    }

    openTaskDetailDialog(user: UserProfile, taskWithInfo: RetrieveTaskByIdReply): MatDialogRef<TaskDetailDialogComponent, any> {
        return this._matDialog.open(TaskDetailDialogComponent, {
            data: {user, taskWithInfo},
            width: '80vw',
            height: '80vh'
        });
    }

    openRequestGoLiveDataDialog(data: RequestGoLiveDialogData): MatDialogRef<RequestGoLiveDialogComponent> {
        return this._matDialog.open(RequestGoLiveDialogComponent, {
            width: '50vw',
            height: '70vh',
            minWidth: '410px',
            minHeight: '500px',
            maxWidth: '410px',
            maxHeight: data?.dtypeToGoLiveRequest ? '615px' : '500px',
            data: data
        });
    }

    openScheduleGoLiveDataDialog(data: RequestGoLiveBody): MatDialogRef<ScheduleGoLiveDialogComponent> {
        return this._matDialog.open(ScheduleGoLiveDialogComponent, {
            width: '50vw',
            height: '70vh',
            minWidth: '410px',
            minHeight: '600px',
            maxWidth: '410px',
            maxHeight: '600px',
            data: data
        });
    }

    openChangeRepoNameDialog(data: string): MatDialogRef<ChangeRepoNameComponent> {
        return this._matDialog.open(ChangeRepoNameComponent, {
            width: '50vw',
            height: '70vh',
            minWidth: '500px',
            minHeight: '400px',
            maxWidth: '500px',
            maxHeight: '400px',
            data: data
        });
    }

    openUploadExcelDialog(dType: DType, minHeight: string): MatDialogRef<UploadExcelDialogComponent> {
        const data = { dType };
        return this._matDialog.open(UploadExcelDialogComponent, {
            data,
            minWidth: '900px',
            maxWidth: '900px',
            minHeight: minHeight,
        });
    }

    openSelectGoalDate(): MatDialogRef<SelectGoalDateComponent> {
        return this._matDialog.open(SelectGoalDateComponent, {
            minWidth: '600px',
            maxWidth: '300px',
        });
    }

    openUserPrivilegeDialog(privilege: UserNodePrivilegesForDisplay, readonly: boolean): MatDialogRef<UserPrivilegeDialogComponent> {
        return this._matDialog.open(UserPrivilegeDialogComponent, {
            data: { privilege, readonly },
            panelClass: 'user-privilege-dialog-panel',
            width: '50vw',
            height: '70vh'
        })
    }
    
    openLocationChangeDialog(item: any): MatDialogRef<LocationChangeDialogComponent> {
        return this._matDialog.open(LocationChangeDialogComponent, {
            panelClass: 'location-change-dialog-panel',
            width: '90vw',
            height: '80vh',
            data: {
                item
            }
        });
    }

    openAliasDialog(resource: ServiceUserAssignment, area: PlanningArea): MatDialogRef<AliasDialogComponent> {
        return this._matDialog.open(AliasDialogComponent, {
            panelClass: 'alias-dialog-panel',
            width: '50vw',
            height: '70vh',
            data: {
                resource,
                area
            }
        });
    }
    
    openClipboardDataDialog(clipboardItem: Clipboard) {
        return this._matDialog.open(ClipboardDataDialogComponent, {
            panelClass: 'clipboard-data-dialog-panel',
            width: '50vw',
            maxHeight: '40vh',
            data: {
                clipboardItem
            }
        })
    }

    openResourcesSelectAreaDialog(planningAreas: PlanningArea[]) {
        return this._matDialog.open(ResourcesSelectAreaDialog, {
            panelClass: 'resources-select-area-dialog-panel',
            data: { planningAreas },
            width: '40vw',
            height: '30vh',
            minWidth: '700px',
            minHeight: '300px',
        })
    }
    
    openSelectLocatorDialog(marca_code?: string) {
        return this._matDialog.open(SelectLocatorDialogComponent, {
            panelClass: 'select-locator-dialog-panel',
            width: '30vw',
            height: '50vh',
            data: {
                marca_code
            }
        })
    }

    openReviewAbsenceDialog(availability: AvailabilityControl, locator: NextLocator, service: NextServicio, readonly = false) {
        return this._matDialog.open(ReviewAbsenceDialogComponent, {
            panelClass: 'review-absence-dialog-panel',
            width: '60vw',
            height: '70vh',
            data: {
                availability,
                locator,
                service,
                readonly
            }
        });
    }

    openCreateProfileDialog(marca_code?: string) {
        return this._matDialog.open(CreateProfileDialogComponent, {
            panelClass: 'create-profile-dialog-panel',
            width: '30vw',
            height: '60vh',
            data: {
                marca_code
            }
        })
    }
    
    openNewReviewAbsenceDialog(formUri: string, locator: NextLocator) {
        return this._matDialog.open(NewReviewAbsenceDialogComponent, {
            panelClass: 'new-review-absence-dialog-panel',
            width: '60vw',
            height: '70vh',
            data: {
                formUri,
                locator
            }
        });
    }

    openEditCalendarDialog(locator: NextLocator, calendarList: NextCalendar[], readonly = false, calendar?: NextCalendar) {
        return this._matDialog.open(EditCalendarDialogComponent, {
            panelClass: 'edit-calendar-dialog-panel',
            width: '80vw',
            height: '80vh',
            data: {
                calendar,
                calendarList,
                readonly,
                locator
            }
        });
    }

    openAbsenceCalendarDialog() {
        return this._matDialog.open(AbsenceCalendarDialogComponent, {
            panelClass: 'edit-calendar-dialog-panel',
            width: '80vw',
            height: '80vh',
        });
    }

    openPresenceConceptDialog(dTypeForm: DTypeForm, namesInUse: string[], service: NextServicio) {
        return this._matDialog.open(PresenceConceptDialogComponent, {
            panelClass: 'presence-concept-dialog-panel',
            width: '70vw',
            height: '70vh',
            data: {
                dTypeForm,
                namesInUse,
                service
            }
        });
    }

    openWorkplaceUserAssignmentDialog(dtype: Signal<DType>, user: ServiceWorkplaceUserAssignment, service: NextServicio) {
        return this._matDialog.open(WorkplaceUserAssignmentDialogComponent, {
            panelClass: 'workplace-user-assignment-dialog-panel',
            width: '70vw',
            height: '70vh',
            data: {
                dtype,
                user,
                service
            }
        });
    }

    openEditUserEventsDialog(userEvent: WorkshiftUserEventSummary, inProgress: boolean) {
        return this._matDialog.open(EditUserEventsDialogComponent, {
            panelClass: 'edit-user-events-dialog-panel',
            width: '60vw',
            height: '70vh',
            data: {
                userEvent,
                inProgress
            }
        })
    }

    openWorkshiftUserEventsDialog(userId: string, workshiftId: string, serviceId: string, userName: string, externalWorkshiftId: string) {
        return this._matDialog.open(WorkshiftUserEventsDialogComponent, {
            panelClass: 'edit-workshift-user-events-dialog-panel',
            width: '30vw',
            height: '60vh',
            data: {
                userId,
                workshiftId,
                serviceId,
                userName,
                externalWorkshiftId
            }
        })
    }

    openSyncWorkplaceUsersDialog(serviceId: string, date: Date): MatDialogRef<SyncWorkplaceUsersDialog, any> {
        return this._matDialog.open(SyncWorkplaceUsersDialog, {
            data: { serviceId, date },
            width: '40vw',
            height: '30vh',
        });
    }

    openSearchUserEventDialog(workshiftDate: string, serviceId: string, workplaceCode: string, usersWithEvents: string[]) {
        return this._matDialog.open(SearchUserEventDialogComponent, {
            data: { workshiftDate, serviceId, workplaceCode, usersWithEvents },
            width: '40vw',
            height: '30vh',
        })
    }

    openAddUserEventDialog(user: WorkshiftResource, workshift: Workshift) {
        return this._matDialog.open(AddUserEventDialogComponent, {
            panelClass: 'edit-user-events-dialog-panel',
            width: '60vw',
            height: '70vh',
            data: {
                user,
                workshift
            }
        })
    }

    openMonthConceptsDialog(period: string, year: string, premises?: PremisesLink, calendarToken?: string, premisesCode?: string) {
        return this._matDialog.open(MonthConceptsDialogComponent, {
            panelClass: 'month-concepts-dialog-panel',
            width: '1800px',
            minHeight: '400px',
            maxHeight: '70vh',
            data: {
                period,
                year,
                premises,
                calendarToken,
                premisesCode,
            }
        });
    }

    openCenterMonthConceptsDialog(period: string, year: string, premises?: PremisesLink, calendarToken?: string, premisesCode?: string) {
        return this._matDialog.open(MonthConceptsDialogComponent, {
            panelClass: 'month-concepts-dialog-panel',
            width: '1800px',
            minHeight: '400px',
            maxHeight: '70vh',
            data: {
                period,
                year,
                premises,
                calendarToken,
                premisesCode,
                isCenterPremisesType: true
            }
        });
    }

    openHotelMonthConceptsDialog(period: string, year: string, premises?: PremisesLink, calendarToken?: string, premisesCode?: string) {
        return this._matDialog.open(HotelMonthConceptsDialogComponent, {
            panelClass: 'month-concepts-dialog-panel',
            width: '1800px',
            minHeight: '400px',
            maxHeight: '70vh',
            data: {
                period,
                year,
                premises,
                calendarToken,
                premisesCode
            }
        });
    }

    openUserQrDialog(assignment: ServiceWorkplaceUserAssignment): MatDialogRef<UserQrDialogComponent, any> {
        return this._matDialog.open(UserQrDialogComponent, {
            data: {
                assignment
            },
            width: '600px',
            height: '530px'
        });
    }

    openWorkdayEventListDialog(workdayEventList: WorkDayEvent[], service: NextServicio): MatDialogRef<WorkdayEventListDialogComponent, any> {
        return this._matDialog.open(WorkdayEventListDialogComponent, {
            panelClass: 'no-padding-dialog',
            data: {
                workdayEventList,
                service
            },
            width: '600px',
            minHeight: '300px',
            maxHeight: '90vh'
        });
    }

    openGeopositionDialog(geopositionSummaryList: WorkdayGeopositionMarkerData[], referenceGeopositionSummaryList: WorkdayReferenceGeopositionMarkerData[], initialMapCenterPosition?: LatLng): MatDialogRef<GeopositionDialogComponent, any> {
        return this._matDialog.open(GeopositionDialogComponent, {
            panelClass: 'no-padding-dialog',
            height: '70vh',
            width: '50vw',
            data: {
                geopositionSummaryList,
                referenceGeopositionSummaryList,
                initialMapCenterPosition
            }
        });
    }
}
